import { useMemo, type ReactNode } from 'react';

// A shopper assistant's answer, read in a transcript, drawn the way the shopper's widget draws it
// (services/storefront-assistant/widget-ui/answer-text.js): the same small, closed set of blocks the
// model writes in plain Markdown, and nothing more:
//   - paragraphs, with line breaks;
//   - section titles: a line that starts with #, ## or ###;
//   - lists, and numbered steps: a numbered list that counts 1, 2, 3 from the start;
//   - tables: a header row, the |---| rule under it, then the rows; a cell that is all **bold** is the
//     row's best value, quietly marked; a wide table scrolls inside the answer;
//   - a fact sheet: a table without a header row and two cells a row, drawn as names and values;
//   - a note: lines that start with >, for a caveat or one thing worth knowing.
// Inside them, **bold** and links: [words](address), or a bare address, which shows as its host and
// path. This is formatting by the text's own marks, never a reading of what the words say. It is never
// parsed as HTML: every piece of the text becomes a React text node, and what is written imperfectly
// still reads as text. A link is a web address (https) or it stays its words: a script, data or
// plain-http address is never made a link.

const tableCells = (line: string) => {
  const row = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return row.split('|').map((cell) => cell.trim());
};
const isRule = (cells: string[]) => cells.every((cell) => /^:?-+:?$/.test(cell));
// A list item's marker, with the model's own number when it is numbered.
const listItem = (line: string) => /^\s*(?:[-*•]|(\d+)[.)])\s+/.exec(line);
const indented = (line: string) => /^\s{2,}\S/.test(line);
const heading = (line: string) => /^\s{0,3}(#{1,6})\s+/.exec(line);

// A link written as [words](address), or a bare web address. The address of a bare one stops before
// closing punctuation and bold marks. The words may hold one level of brackets (`[a [b] c](…)`), the
// address one level of balanced parentheses (`…/Foo_(bar)`), and a "title" after it is dropped.
const WORDS = String.raw`((?:[^\[\]\n]|\[[^\[\]\n]*\])+)`;
const ADDRESS = String.raw`<?((?:[^\s()<>]|\([^\s()<>]*\))+)>?`;
const TITLE = String.raw`(?:\s+(?:"[^"\n]*"|'[^'\n]*'))?`;
const BARE = String.raw`(https?:\/\/[^\s<>"'()[\]*”»]+)`;
const LINKS = new RegExp(String.raw`\[${WORDS}\]\(\s*${ADDRESS}${TITLE}\s*\)|${BARE}`, 'g');
const trailing = (url: string) => {
  let end = url.length;
  while (end && '.,;:!?'.includes(url[end - 1])) end -= 1;
  return url.slice(0, end);
};
// Links are looked for in a line of an answer's ordinary length; a longer one stays words, so a
// degenerate line costs no more than its length.
const LINK_LINE = 4000;

type LinkPart = { url: string; words: string | null };
/** The text split into its words and its links ({url, words}; a bare address has no words). */
function linkParts(text: string): (string | LinkPart)[] {
  const parts: (string | LinkPart)[] = [];
  let at = 0;
  for (const match of text.length > LINK_LINE ? [] : text.matchAll(LINKS)) {
    const bare = match[3] ? trailing(match[3]) : null;
    parts.push(text.slice(at, match.index));
    parts.push({
      url: bare ?? match[2],
      words: bare ? null : match[1].replaceAll('**', ''),
    });
    at = match.index + (bare ?? match[0]).length;
  }
  parts.push(text.slice(at));
  return parts;
}

/** A web address (https) as an address, or null: anything else is not made a link. */
export function webAddress(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}
const siteOf = (href: string) => new URL(href).host.replace(/^www\./, '');
function shortAddress(href: string) {
  const url = new URL(href);
  const text = `${url.host.replace(/^www\./, '')}${url.pathname.replace(/\/+$/, '')}`;
  return text.length > 42 ? `${text.slice(0, 41)}…` : text;
}

// A link off the shop, in a new tab. The words may claim any destination, so a link with words also
// names the site it opens (a bare address already shows it).
function linkOf(part: LinkPart, key: string): ReactNode {
  const href = webAddress(part.url);
  if (!href) return part.words ?? part.url;
  return (
    <a key={key} className="sf-tr-rich-link" href={href} target="_blank" rel="noopener noreferrer">
      {part.words ?? shortAddress(href)}
      {part.words ? <span className="sf-tr-rich-site"> ({siteOf(href)})</span> : null}
    </a>
  );
}

/** Text with its **bold** and links: plain words, <strong>, and links that are safe to open. */
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let strong = false;
  const add = (node: ReactNode) =>
    out.push(strong ? <strong key={`b${out.length}`}>{node}</strong> : node);
  for (const part of linkParts(text)) {
    if (typeof part !== 'string') {
      add(linkOf(part, `l${out.length}`));
      continue;
    }
    part.split('**').forEach((piece, i) => {
      if (i) strong = !strong;
      if (piece) add(piece);
    });
  }
  return out;
}

// Lines of a paragraph or a note, one line break between them.
const lines = (rows: string[]): ReactNode[] =>
  rows.flatMap((row, i) => [i ? <br key={`br${i}`} /> : null, ...inline(row)]);

export type Block =
  | { kind: 'text'; rows: string[] }
  | { kind: 'heading'; text: string }
  | { kind: 'note'; paragraphs: string[][] }
  | { kind: 'facts'; rows: string[][] }
  | {
      kind: 'table';
      head: string[] | null;
      body: string[][];
      aligns: (string | null)[];
      rowNames: boolean;
    }
  | {
      kind: 'list';
      ordered: boolean;
      items: { value: number | null; text: string; more: Block[] }[];
    };

// A header row with nothing in it (`| | |` over its rule) is no header: the rows alone.
const emptyHead = (rows: string[][]) =>
  rows.length > 1 && isRule(rows[1]) && rows[0].every((cell) => !cell);

function tableOrFacts(rowLines: string[]): Block | null {
  let rows = rowLines.map(tableCells);
  if (emptyHead(rows)) rows = rows.slice(2);
  // Only an empty header and its rule: nothing to draw.
  if (!rows.length) return null;
  const rule = rows.length > 1 && isRule(rows[1]) ? rows[1] : null;
  if (!rule && rows.every((cells) => cells.length === 2)) return { kind: 'facts', rows };
  const align = (i: number) => {
    const cell = rule?.[i] ?? '';
    if (cell.endsWith(':')) return cell.startsWith(':') ? 'center' : 'right';
    return null;
  };
  return {
    kind: 'table',
    head: rule ? rows[0] : null,
    body: rule ? rows.slice(2) : rows,
    aligns: Array.from({ length: Math.max(...rows.map((cells) => cells.length)) }, (_, i) =>
      align(i),
    ),
    // When the header row's first cell is empty, the first cell of each row names that row.
    rowNames: Boolean(rule) && rows[0][0] === '',
  };
}

// Each numbered item keeps its own number, so a list split by blank lines counts on and a line such
// as "24. Dezember" stays the 24th.
function listBlock(rows: string[], ordered: boolean): Block {
  return {
    kind: 'list',
    ordered,
    items: rows.map((line) => {
      const marker = listItem(line)!;
      return {
        value: marker[1] ? Number(marker[1]) : null,
        text: line.slice(marker[0].length),
        more: [],
      };
    }),
  };
}

// A note: the lines after their >, a blank line between paragraphs.
function noteBlock(rows: string[]): Block {
  const paragraphs: string[][] = [[]];
  for (const line of rows.map((text) => text.trim().replace(/^>\s?/, ''))) {
    if (line.trim()) paragraphs[paragraphs.length - 1].push(line);
    else if (paragraphs[paragraphs.length - 1].length) paragraphs.push([]);
  }
  return { kind: 'note', paragraphs: paragraphs.filter((paragraph) => paragraph.length) };
}

// Lines under a list item that are not items themselves (`more`) are a paragraph inside it.
function blockOf(kind: string, rows: string[]): Block | null {
  if (kind.startsWith('ol') || kind.startsWith('ul')) return listBlock(rows, kind.startsWith('ol'));
  if (kind === 'table') return tableOrFacts(rows);
  if (kind === 'note') return noteBlock(rows);
  if (kind === 'heading')
    return { kind: 'heading', text: rows[0].slice(heading(rows[0])![0].length) };
  return { kind: 'text', rows };
}

const WHOLE = new Set(['table', 'note', 'heading', 'text']);
const inList = (kind: string | null) => Boolean(kind) && !WHOLE.has(kind!);
function lineKind(line: string, kind: string | null): string | null {
  if (!line.trim()) return null;
  const start = line.trim()[0];
  if (start === '|') return 'table';
  if (start === '>') return 'note';
  if (heading(line)) return 'heading';
  const marker = listItem(line);
  if (!marker) return indented(line) && inList(kind) ? 'more' : 'text';
  return `${marker[1] ? 'ol' : 'ul'}${indented(line) ? ' in' : ''}`;
}

// Indented items and lines under a list item belong to that item; a list that goes on after a blank
// line or an item's own points stays one list.
function place(blocks: Block[], block: Block, nested: boolean) {
  const last = blocks.at(-1);
  if (nested && last?.kind === 'list') {
    const inside = last.items.at(-1)!.more;
    const before = inside.at(-1);
    if (block.kind === 'list' && before?.kind === 'list' && before.ordered === block.ordered)
      before.items.push(...block.items);
    else inside.push(block);
  } else if (
    !nested &&
    block.kind === 'list' &&
    last?.kind === 'list' &&
    last.ordered === block.ordered
  )
    last.items.push(...block.items);
  else blocks.push(block);
}

/** The blocks of an answer's words. */
export function answerBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let kind: string | null = null;
  let rows: string[] = [];
  const close = () => {
    if (!rows.length || !kind) return;
    const block = blockOf(kind, rows);
    if (block) place(blocks, block, indented(rows[0]));
    rows = [];
  };
  for (const line of text.split('\n')) {
    const next = lineKind(line, kind);
    if (next !== kind || next === 'heading') close();
    kind = next;
    if (next) rows.push(line);
  }
  close();
  return blocks;
}

// A numbered list that counts 1, 2, 3 from its start is a list of steps.
const isSteps = (block: Extract<Block, { kind: 'list' }>) =>
  block.ordered &&
  block.items.length > 1 &&
  block.items.every((item, index) => item.value === index + 1);

const cellName = (cell: string) => cell.replaceAll('**', '');
// A cell that is all bold is that row's best value.
const allBold = (cell: string) => /^\*\*[^*]+\*\*$/.test(cell);

function TableView({ block }: { block: Extract<Block, { kind: 'table' }> }) {
  const { head, body, aligns, rowNames } = block;
  const label = (head ?? body[0] ?? []).map(cellName).filter(Boolean).join(', ');
  return (
    <div
      className="sf-tr-rich-table"
      tabIndex={0}
      role="region"
      aria-label={label || undefined}
      data-testid="sf-answer-table"
    >
      <table>
        {head && (
          <thead>
            <tr>
              {head.map((cell, i) => (
                <th key={i} scope="col" data-align={aligns[i] ?? undefined}>
                  {inline(cell)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {body.map((cells, r) => (
            <tr key={r}>
              {cells.map((cell, i) =>
                rowNames && i === 0 ? (
                  <th key={i} scope="row" data-align={aligns[i] ?? undefined}>
                    {inline(cell)}
                  </th>
                ) : (
                  <td
                    key={i}
                    data-align={aligns[i] ?? undefined}
                    data-best={i > 0 && allBold(cell) ? 'true' : undefined}
                  >
                    {inline(cell)}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ListView({ block }: { block: Extract<Block, { kind: 'list' }> }) {
  const Tag = block.ordered ? 'ol' : 'ul';
  const steps = isSteps(block);
  return (
    <Tag {...(steps ? { 'data-steps': '', role: 'list' } : {})}>
      {block.items.map((item, i) => (
        <li key={i} {...(block.ordered && item.value !== null ? { value: item.value } : {})}>
          {inline(item.text)}
          {item.more.map((inside, j) => (
            <BlockView key={j} block={inside} />
          ))}
        </li>
      ))}
    </Tag>
  );
}

function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case 'heading':
      return <h3 className="sf-tr-rich-h">{inline(block.text)}</h3>;
    case 'list':
      return <ListView block={block} />;
    case 'table':
      return <TableView block={block} />;
    case 'facts':
      return (
        <dl className="sf-tr-rich-facts">
          {block.rows.map(([name, value], i) => (
            <div key={i}>
              <dt>{inline(name)}</dt>
              <dd>{inline(value)}</dd>
            </div>
          ))}
        </dl>
      );
    case 'note':
      return (
        <div className="sf-tr-rich-note" role="note">
          {block.paragraphs.map((rows, i) => (
            <p key={i}>{lines(rows)}</p>
          ))}
        </div>
      );
    default:
      return <p>{lines(block.rows)}</p>;
  }
}

/** An assistant's answer in a transcript: its blocks, read only. */
export function AnswerText({ text }: { text: string }) {
  const blocks = useMemo(() => answerBlocks(text), [text]);
  return (
    <div className="sf-tr-rich" data-testid="sf-answer-text">
      {blocks.map((block, i) => (
        <BlockView key={i} block={block} />
      ))}
    </div>
  );
}
